import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Icon } from "@/components/ui/Icon";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { resolveWorkspaceColor } from "@/lib/colors";
import { formatSeriesSchedule } from "@/lib/format";
import { t } from "@/lib/strings";
import { showToast } from "@/lib/toast";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useLocalNow } from "@/hooks/useLocalNow";
import { useSeries, useDeleteSeries } from "@/hooks/useMeetingSeries";
import { useSeriesMemos } from "@/hooks/useMemo";
import { useSeriesTasks } from "@/hooks/useSeriesTasks";
import { NewMemoDialog } from "@/components/dialogs/NewMemoDialog";
import { Dialog } from "@/components/ui/Dialog";
import { EditSeriesDialog } from "@/components/dialogs/EditSeriesDialog";
import { ParticipantList } from "@/components/memo/ParticipantList";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { SeriesTasksList } from "@/components/memo/SeriesTasksList";
import {
  ProtokolleHeader,
  primaryButtonStyle,
  ghostButtonStyle,
} from "@/components/memo/ui/ProtokolleHeader";
import { MetaChip } from "@/components/memo/ui/MetaChip";
import { SeriesMemoList } from "@/components/memo/SeriesMemoList";
import { BRAND } from "@/lib/tokens";

export function SeriesPage() {
  const { seriesId } = useParams({ strict: false }) as { seriesId: string };
  const { data: series } = useSeries(seriesId);
  const { data: memos = [] } = useSeriesMemos(seriesId);
  const { data: tasks = [] } = useSeriesTasks(seriesId);
  const { data: workspaces = [] } = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: () => api.listWorkspaces(),
  });
  const isDesktop = useBreakpoint("md");
  const now = useLocalNow();
  const navigate = useNavigate();
  const deleteSeries = useDeleteSeries(seriesId);
  const [newOpen, setNewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [partsOpen, setPartsOpen] = useState(false);
  const [tab, setTab] = useState<"memos" | "tasks">("memos");

  const latestMemoId = memos[0]?.id ?? null;
  const boardId = series?.aufgaben_board_id ?? null;
  // "Neues Protokoll" is the "Bearbeiter für finalisierte Protokolle" right, not
  // admin-only — see the header button below for why.
  const canCreate = Boolean(series?.access.can_admin || series?.access.can_edit_finalized);

  const ws = series ? workspaces.find((w) => w.id === series.home_workspace_id) : undefined;
  const deptColor = series ? resolveWorkspaceColor(ws, series.home_workspace_id) : BRAND.blue;
  const kickerLabel = ws ? `${t.seriesKicker} · ${ws.name}` : t.seriesKicker;
  const cadence = series
    ? formatSeriesSchedule(series.default_weekday, series.default_start_time)
    : "";

  const openBoardAction =
    tab === "tasks" && boardId ? (
      <Link
        to="/b/$boardId"
        params={{ boardId }}
        style={{ ...ghostButtonStyle, textDecoration: "none" }}
      >
        <Icon name="kanban" size={15} color="var(--ink-2)" />
        {t.seriesOpenTasksBoard}
      </Link>
    ) : null;

  const tabs = [
    { key: "memos" as const, label: t.seriesTabMemos, count: memos.length },
    { key: "tasks" as const, label: t.seriesTabTasks, count: tasks.length },
  ];

  return (
    <div style={{ padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <Breadcrumb
          items={[
            { label: t.navHome, to: "/" },
            { label: t.meetingsTitle, to: "/meetings" },
            { label: series?.name ?? "…" },
          ]}
        />

        <div style={{ marginTop: 8 }}>
          <ProtokolleHeader
            kicker={{ color: deptColor, label: kickerLabel }}
            title={series?.name ?? "…"}
            actions={
              <>
                {series?.access.can_admin ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditOpen(true)}
                      style={ghostButtonStyle}
                    >
                      <Icon name="settings" size={15} color="var(--ink-2)" />
                      {t.seriesEdit}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteOpen(true)}
                      style={ghostButtonStyle}
                    >
                      <Icon name="trash" size={15} color="var(--danger)" />
                      {t.seriesDelete}
                    </button>
                  </>
                ) : null}
                {openBoardAction}
                {/* "Neues Protokoll" is the "Bearbeiter für finalisierte Protokolle" right, not
                    admin-only: both routes behind it (create + copy-forward) now call
                    require_trusted_edit, so an editor may mint the next occurrence exactly as
                    they finalize/delete. Mirror the server gate — can_edit_finalized ∪ can_admin.
                    "Reihe bearbeiten"/"Reihe löschen" above stay can_admin (that is where the
                    editor right is assigned). */}
                {canCreate ? (
                  <button type="button" onClick={() => setNewOpen(true)} style={primaryButtonStyle}>
                    <Icon name="plus" size={15} color="#fff" />
                    {t.seriesNewMemo}
                  </button>
                ) : null}
              </>
            }
            chips={
              series ? (
                <>
                  {cadence ? <MetaChip icon="refresh">{cadence}</MetaChip> : null}
                  {/* 🔴 A BUTTON, not decoration. It read "10 Teilnehmer" and did nothing —
                      the same inert chip the memo header had. Read-only for everyone,
                      including admins: they change the roster in "Reihe bearbeiten", which
                      owns name/Abschnitte/Teilnehmer/Bearbeiter together. A second edit path
                      here would be a second source of truth for the same list. */}
                  <button
                    type="button"
                    onClick={() => setPartsOpen(true)}
                    style={{ all: "unset", cursor: "pointer" }}
                  >
                    <MetaChip icon="group">
                      {series.participants.length} {t.seriesMetaParticipants}
                    </MetaChip>
                  </button>
                  <MetaChip icon="file">
                    {memos.length}{" "}
                    {memos.length === 1 ? t.seriesMetaMemosSingular : t.seriesMetaMemos}
                  </MetaChip>
                </>
              ) : null
            }
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 4,
            marginTop: 20,
            borderBottom: "1px solid var(--hairline)",
          }}
        >
          {tabs.map((tb) => {
            const active = tab === tb.key;
            return (
              <button
                key={tb.key}
                type="button"
                aria-pressed={active}
                onClick={() => setTab(tb.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 4px",
                  marginRight: 16,
                  fontSize: 13.5,
                  fontWeight: 600,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: active ? "var(--ink-1)" : "var(--ink-2)",
                  borderBottom: active ? `2px solid ${BRAND.blue}` : "2px solid transparent",
                  marginBottom: -1,
                }}
              >
                {tb.label}
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: active ? "var(--ink-2)" : "var(--ink-3)",
                    background: "var(--surface-soft)",
                    borderRadius: 999,
                    padding: "1px 7px",
                  }}
                >
                  {tb.count}
                </span>
              </button>
            );
          })}
        </div>

        {tab === "memos" ? (
          <div style={{ marginTop: 20 }}>
            <SeriesMemoList
              seriesId={seriesId}
              memos={memos}
              now={now}
              defaultStartTime={series?.default_start_time ?? null}
              canCreate={canCreate}
              onNewMemo={() => setNewOpen(true)}
            />
          </div>
        ) : (
          <div style={{ marginTop: 20 }}>
            <SeriesTasksList seriesId={seriesId} boardId={boardId} />
          </div>
        )}
      </div>

      {series ? (
        <NewMemoDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          series={series}
          sourceMemoId={latestMemoId}
        />
      ) : null}
      {series ? (
        <>
          <EditSeriesDialog open={editOpen} onOpenChange={setEditOpen} series={series} />
          <Dialog
            open={partsOpen}
            onOpenChange={setPartsOpen}
            title={t.seriesParticipantsTitle}
            width={480}
          >
            <div style={{ marginTop: 6 }}>
              <ParticipantList people={series.participants} />
            </div>
          </Dialog>
        </>
      ) : null}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t.seriesDeleteTitle}
        message={t.seriesDeleteMessage}
        confirmLabel={t.seriesDelete}
        tone="danger"
        busy={deleteSeries.isPending}
        onConfirm={() => {
          deleteSeries.mutate(undefined, {
            onSuccess: () => {
              setDeleteOpen(false);
              showToast(t.seriesDeleted, "success");
              void navigate({ to: "/meetings" });
            },
          });
        }}
      />
    </div>
  );
}
